// ============================================================================
// ReconciliationPanel — Personal Finance V2 (Blueprint §6.2 / Fase 5+)
// Painel de conciliação Firestore ↔ Bling — estilo Windows/Fluent.
// Mostra: KPIs Bling PJ, health score, divergências, meta do snapshot.
// ============================================================================

import React from 'react';
import { DIVERGENCE_TYPES } from '../services/reconcileService.js';

const TYPE_LABELS = {
    [DIVERGENCE_TYPES.ONLY_BLING]:      { label: 'Só no Bling',     bg: 'bg-[#e8f0fe]', text: 'text-[#1a56db]', icon: 'fas fa-cloud' },
    [DIVERGENCE_TYPES.ONLY_FIRESTORE]:  { label: 'Só no Firestore', bg: 'bg-[#fff7ed]', text: 'text-[#c2410c]', icon: 'fas fa-database' },
    [DIVERGENCE_TYPES.AMOUNT_MISMATCH]: { label: 'Valor diverge',   bg: 'bg-[#fef2f2]', text: 'text-[#dc2626]', icon: 'fas fa-not-equal' },
    [DIVERGENCE_TYPES.STATUS_MISMATCH]: { label: 'Status diverge',  bg: 'bg-[#fffbeb]', text: 'text-[#d97706]', icon: 'fas fa-exchange-alt' },
};

const fmt = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

// Windows-style KPI tile
function KpiTile({ label, value, sub, accent = 'text-[#20243B]', icon }) {
    return (
        <div className="bg-white border border-[#e5e7eb] px-3 py-2 min-w-[140px] flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
                {icon && <i className={`${icon} text-[9px] text-[#6b7280]`}></i>}
                <span className="text-[9px] font-semibold text-[#6b7280] uppercase tracking-wider">{label}</span>
            </div>
            <div className={`font-mono font-bold text-[14px] tabular-nums leading-tight ${accent}`}>{value}</div>
            {sub && <div className="text-[9px] text-[#9ca3af] mt-0.5">{sub}</div>}
        </div>
    );
}

export function ReconciliationPanel({ result, health, hasRun, onRun, blingLoading, blingError, onRefreshBling, onVincular }) {
    const kpis = result?.blingKpis;

    return (
        <div className="space-y-0">
            {/* ── TOOLBAR ── */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#f3f4f6] border-b border-[#d1d5db]">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-[#374151] uppercase tracking-wide">
                        <i className="fas fa-balance-scale mr-1.5 text-[#6b7280]"></i>Conciliação Firestore ↔ Bling
                    </span>
                    {result?.blingMeta && (
                        <span className="text-[9px] text-[#9ca3af]">
                            Snapshot: {new Date(result.blingMeta.generatedAt).toLocaleString('pt-BR')}
                            {result.blingMeta.counts && (
                                <span className="ml-1">({result.blingMeta.counts.contatos} contatos, {result.blingMeta.counts.formasPgto} formas pgto, {result.blingMeta.counts.categorias} categorias)</span>
                            )}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {blingLoading && <i className="fas fa-spinner fa-spin text-[10px] text-[#6b7280]"></i>}
                    {blingError && <span className="text-[9px] text-[#dc2626]">{blingError.code === 'SNAPSHOT_NOT_FOUND' ? 'Snapshot ausente' : 'Erro'}</span>}
                    <button onClick={onRefreshBling} className="text-[9px] text-[#2563eb] hover:underline font-semibold" title="Atualizar snapshot do Bling">
                        <i className="fas fa-sync mr-0.5"></i>Atualizar
                    </button>
                    <button
                        onClick={onRun}
                        disabled={blingLoading}
                        className="px-3 py-1 bg-[#20243B] text-white font-bold text-[10px] hover:bg-[#1a1e30] disabled:opacity-40 border border-[#20243B]"
                    >
                        <i className="fas fa-play mr-1"></i>{hasRun ? 'Re-executar' : 'Conciliar'}
                    </button>
                </div>
            </div>

            {/* ── KPI STRIP (Bling PJ + Health) ── */}
            <div className="flex flex-wrap border-b border-[#e5e7eb]">
                <KpiTile
                    label="Saúde dos Pares"
                    value={`${health.score}%`}
                    sub={`${health.completePairs} pares · ${health.singles} solo · ${health.orphans} órfãos`}
                    accent={health.score === 100 ? 'text-[#059669]' : health.score >= 80 ? 'text-[#d97706]' : 'text-[#dc2626]'}
                    icon={health.score === 100 ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'}
                />
                {kpis && (
                    <>
                        <KpiTile label="A Receber (PJ)" value={fmt(kpis.total_a_receber)} sub={`${kpis.qtd_contas_receber_total} títulos`} accent="text-[#059669]" icon="fas fa-arrow-down" />
                        <KpiTile label="A Pagar (PJ)" value={fmt(kpis.total_a_pagar)} sub={`${kpis.qtd_contas_pagar_total} títulos · ${kpis.qtd_pagar_vencidos} vencidos`} accent="text-[#dc2626]" icon="fas fa-arrow-up" />
                        <KpiTile label="Saldo Projetado" value={fmt(kpis.saldo_projetado)} accent={kpis.saldo_projetado >= 0 ? 'text-[#059669]' : 'text-[#dc2626]'} icon="fas fa-chart-line" />
                        <KpiTile label="Pipeline Propostas" value={fmt(kpis.valor_pipeline_propostas)} sub={`${kpis.qtd_propostas_abertas} abertas`} accent="text-[#6366f1]" icon="fas fa-briefcase" />
                    </>
                )}
            </div>

            {/* ── PRE-RUN STATE ── */}
            {!hasRun && !blingLoading && (
                <div className="text-center py-6 text-[#9ca3af]">
                    <i className="fas fa-balance-scale text-2xl mb-2 block"></i>
                    <p className="text-[11px]">Clique em <strong>Conciliar</strong> para comparar postings Firestore com o snapshot Bling.</p>
                </div>
            )}

            {/* ── RESULTS ── */}
            {hasRun && result && (
                <>
                    {/* Summary bar */}
                    <div className="flex items-center gap-1 px-3 py-1 bg-[#fafafa] border-b border-[#e5e7eb] text-[9px] font-semibold text-[#6b7280]">
                        <span className="px-1.5 py-0.5 bg-[#f3f4f6] border border-[#d1d5db]">Firestore: {result.summary.totalFirestore}</span>
                        <span className="px-1.5 py-0.5 bg-[#e8f0fe] border border-[#bfdbfe] text-[#1d4ed8]">Bling Pagar: {result.summary.totalBlingPagar}</span>
                        <span className="px-1.5 py-0.5 bg-[#e8f0fe] border border-[#bfdbfe] text-[#1d4ed8]">Bling Receber: {result.summary.totalBlingReceber}</span>
                        <span className="px-1.5 py-0.5 bg-[#ecfdf5] border border-[#a7f3d0] text-[#059669]"><i className="fas fa-check mr-0.5"></i>{result.summary.matched} conciliados</span>
                        {result.summary.divergences > 0 ? (
                            <span className="px-1.5 py-0.5 bg-[#fef2f2] border border-[#fecaca] text-[#dc2626]"><i className="fas fa-exclamation-circle mr-0.5"></i>{result.summary.divergences} divergências</span>
                        ) : (
                            <span className="px-1.5 py-0.5 bg-[#ecfdf5] border border-[#a7f3d0] text-[#059669]"><i className="fas fa-check-double mr-0.5"></i>Sem divergências</span>
                        )}
                    </div>

                    {/* Data grid — Windows Explorer style */}
                    {result.divergences.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px] border-collapse">
                                <thead>
                                    <tr className="bg-[#f0f0f0] border-b border-[#c8c8c8]">
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[26px]">#</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[110px]">Tipo</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151]">Detalhe</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[90px]">Contato</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[90px]">Forma Pgto</th>
                                        <th className="px-2 py-1 text-right text-[10px] font-semibold text-[#374151] w-[90px]">Valor</th>
                                        <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[80px]">Vencimento</th>
                                        <th className="px-1 py-1 w-[70px]"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.divergences.map((d, idx) => {
                                        const meta = TYPE_LABELS[d.type] || { label: d.type, bg: 'bg-[#f3f4f6]', text: 'text-[#374151]', icon: 'fas fa-question' };
                                        const val = d.bling?.value || d.firestore?.value || 0;
                                        const date = d.bling?.date || d.firestore?.date || '—';
                                        const contact = d.bling?.contactName || '—';
                                        const forma = d.bling?.formaPgto || '—';
                                        const zebra = idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]';
                                        return (
                                            <tr key={idx} className={`${zebra} border-b border-[#e5e7eb] hover:bg-[#e8f0fe]/40`}>
                                                <td className="px-2 py-1 text-[9px] text-[#9ca3af] tabular-nums">{idx + 1}</td>
                                                <td className="px-2 py-1">
                                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold ${meta.bg} ${meta.text}`}>
                                                        <i className={`${meta.icon} text-[8px]`}></i>{meta.label}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1 text-[#374151] leading-tight">{d.detail}</td>
                                                <td className="px-2 py-1 text-[10px] text-[#6b7280] truncate max-w-[90px]" title={contact}>{contact}</td>
                                                <td className="px-2 py-1 text-[10px] text-[#6b7280] truncate max-w-[90px]" title={forma}>{forma}</td>
                                                <td className="px-2 py-1 text-right font-mono font-bold tabular-nums text-[#374151]">{fmt(val)}</td>
                                                <td className="px-2 py-1 font-mono text-[10px] text-[#6b7280]">{date}</td>
                                                <td className="px-1 py-1 text-center">
                                                    {(d.type === DIVERGENCE_TYPES.ONLY_FIRESTORE || d.type === DIVERGENCE_TYPES.ONLY_BLING) && onVincular && (
                                                        <button
                                                            onClick={() => onVincular(d)}
                                                            className="px-1.5 py-0.5 text-[8px] font-bold text-[#2563eb] border border-[#bfdbfe] bg-[#eff6ff] hover:bg-[#dbeafe]"
                                                            title="Associar lançamento Firestore ↔ Bling"
                                                        >
                                                            <i className="fas fa-link mr-0.5"></i>Vincular
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <i className="fas fa-check-double text-2xl text-[#059669] mb-1 block"></i>
                            <p className="text-[12px] font-bold text-[#059669]">Conciliação OK</p>
                            <p className="text-[10px] text-[#9ca3af]">Nenhuma divergência encontrada entre Firestore e Bling.</p>
                        </div>
                    )}

                    {/* ── STATUS BAR (Windows style) ── */}
                    <div className="flex items-center justify-between px-3 py-1 bg-[#f0f0f0] border-t border-[#c8c8c8] text-[9px] text-[#6b7280]">
                        <span>{result.divergences.length} {result.divergences.length === 1 ? 'divergência' : 'divergências'} · {result.summary.matched} conciliados</span>
                        <span>Snapshot v{result.blingMeta?.version || '?'} · {result.blingMeta?.generatedAt ? new Date(result.blingMeta.generatedAt).toLocaleDateString('pt-BR') : '—'}</span>
                    </div>
                </>
            )}
        </div>
    );
}
